import { PERIPHERAL_TYPE_PRINTER } from "@constants/index";
import { checkIpIfExist, IpResourceEntry } from "@helpers/ipValidation";

/**
 * Shape of the row-in-edit handed to the printer form. The parent passes
 * an empty array `[]` in add mode, so `length` stays optional alongside
 * the row fields. Identity scalars (Id, shopId) are typed strictly
 * because the duplicate-IP check reads them directly; the remaining
 * fields are kept as `any` while the broader edit-flow refactor is
 * staged — legacy callers read them without narrowing.
 */
export interface PrinterEditedData {
    length?: number;
    Id?: string;
    id?: string;
    type?: string;
    shopId?: string;
    peripheralId?: string;
    Marque?: any;
    Remarque?: any;
    port_series?: any;
    path?: any;
    speed?: any;
    code?: any;
    customer_ticket?: any;
    tickets?: any;
    [key: string]: any;
}

/**
 * Pure decision for the printer form's duplicate-IP check. Returns true
 * when the given IP would collide with a non-printer peripheral in the
 * target shop. Printer-to-printer sharing in the same shop is allowed —
 * that exception is enforced inside checkIpIfExist.
 *
 * Live in its own module so the scenarios (edit flow / add flow /
 * printer exception / empty input / multi-shop add) can be unit-tested
 * without pulling the full printer hook and its React / store chain.
 */
export function computePrinterDuplicateIp(
    ip: string,
    editedData: PrinterEditedData,
    localShopId: string[] | undefined,
    resourceIp: IpResourceEntry[]
): boolean {
    if (!ip || ip.trim() === "") return false;
    const isEditing = editedData?.Id !== undefined;
    const targetShopId = isEditing
        ? editedData.shopId
        : localShopId && localShopId.length === 1
        ? localShopId[0]
        : undefined;
    if (!targetShopId) return false;
    return checkIpIfExist(
        resourceIp,
        ip,
        isEditing ? (editedData.Id as string) : "",
        targetShopId,
        PERIPHERAL_TYPE_PRINTER
    );
}
